import { Descriptions, Table, TableProps } from 'antd';
import classNames from 'classnames';

import { ArrowIcon } from '@/icons/ArrowIcon';

import styles from './AntdTable.module.scss';

const onHeaderCell = () => ({
	className: styles.header,
});

type SectionRow = {
	type: 'section';
	sectionTitle?: string;
};

type Props<T extends object> = TableProps<T> & {
	containerClass?: string;
	onMobileClick?: (record: T) => void;
};

const isSectionRow = (record: object): record is SectionRow =>
	'type' in record && record.type === 'section';

export const AntTable = <T extends object>(props: Props<T>) => {
	const columns = props?.columns?.map((col, index) => ({
		...col,
		onHeaderCell: () => {
			const propsValue = col?.onHeaderCell
				? {
						//@ts-ignore
						...col?.onHeaderCell(col, index),
					}
				: {};

			return {
				...onHeaderCell(),
				...propsValue,
			};
		},
	}));

	const getRowKey = (record: T, index: number) => {
		if (typeof props.rowKey === 'function') {
			return props.rowKey(record);
		}

		if (typeof props.rowKey === 'string') {
			return String(record[props.rowKey as keyof T]);
		}

		return index;
	};

	const pagination =
		props.pagination === false
			? false
			: {
					...props.pagination,
					prevIcon: (
						<span className={classNames(styles.paginationArrow, styles.paginationArrowPrev)}>
							<ArrowIcon color="currentColor" width={8} height={12} />
						</span>
					),
					nextIcon: (
						<span className={styles.paginationArrow}>
							<ArrowIcon color="currentColor" width={8} height={12} />
						</span>
					),
				};

	return (
		<>
			<div className={classNames(styles.mobileList, props?.containerClass)}>
				{props?.dataSource?.map((record, index) =>
					isSectionRow(record) ? (
						<div key={getRowKey(record as T, index)} className={styles.sectionTitle}>
							{record.sectionTitle}
						</div>
					) : (
						<div
							key={getRowKey(record, index)}
							className={classNames(styles.card, props.onMobileClick && styles.hoveredCard)}
							onClick={() => props.onMobileClick?.(record)}
						>
							<Descriptions column={1} size="small" styles={{ label: { fontWeight: 500 } }}>
								{columns?.map((column, colIndex) => {
									if (!column || !('dataIndex' in column)) {
										return null;
									}

									const dataIndex = column.dataIndex as keyof T;
									const value = record[dataIndex];

									const renderedValue =
										column.render?.(value, record, colIndex) ?? String(value ?? '-');

									if (!column.title) {
										return renderedValue as React.ReactNode;
									}

									return (
										<Descriptions.Item
											key={String(dataIndex)}
											label={typeof column.title === 'string' ? column.title : '-'}
										>
											{renderedValue as React.ReactNode}
										</Descriptions.Item>
									);
								})}
							</Descriptions>
						</div>
					),
				)}
			</div>

			<div className={classNames(styles.container, props?.containerClass)}>
				<Table
					{...props}
					columns={columns}
					pagination={pagination}
					className={classNames(styles.table, props?.className)}
				/>
			</div>
		</>
	);
};
